import { useState, useRef, useEffect, useMemo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "../../styles/FilterPill.css";
import FilledAddicon from "../../assets/common/filled-plus-icon.svg";
import FilledXicon from "../../assets/common/filled-x-icon.svg";
import { ActionItemGroupByKey, capitalizeFirstLetter, formatPresetLabel, getAvatarColor, getAvatarInitials } from "../../utils/utils";
import FilterDatePicker, { FilterDatePickerHandle } from "./FilterDatePicker";
import GroupByIcon from "../../assets/action-items/groupByIcon.svg";
import ColumnFilter from "./customFilter";
import Avatar from "@mui/material/Avatar";
import { Box, Popper, Tooltip, Typography } from "@mui/material";
import HoveredIcon from "../../assets/common/close-hover.svg";
import InfoIcon from "../../assets/common/info-icon.svg";
import InfoIconHover from "../../assets/common/info-icon-hover.svg";
import { DateRangeValue, Options } from "../../utils/types";

interface LabelOption {
  label: string;
  value: string;
}
interface NumberFilterValue {
  operator: "gt" | "lt";
  value: number;
}
interface FilterPillProps {
  type?: "date" | "select" | "text" | "number";
  label: string | LabelOption[];
  selectedLabel?: string;
  onLabelChange?: (newLabel: LabelOption) => void;
  options?: Options[];
  value?: string | string[] | DateRangeValue | NumberFilterValue;
  displayValue?: string; 
  onChange: (value?: string | string[] | DateRangeValue | NumberFilterValue) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  selectedTab?: string;
  avatar?: string | undefined;
  pillType?: "assignee" | "coach" | "subscribers" | "other";
  displayLabel?: string;
  limitFutureDays?: number
  email?: string;
  showClearIcon?: boolean;
  readOnly?: boolean;
  groupBy?: ActionItemGroupByKey | string;
  displayAll?: boolean;
  parent_page?: "anlytics-page" | "meetings-page" | "tasks-page" | "settings_page" | "accounts_page" | "client_overview_page" | "report_page" | "opportunities-page";
  enableAccountSearch?: boolean;
  forceOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  datePresetOptions?: Options[];
  showDateMenuHeader?: boolean;
  maxRangeInMonths?: number;
  columnKey?: string;
  isEnhancedLabel?: boolean;
  useColumnFilter?: boolean;
  singleSelection?: boolean;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
  hideUntilSearchThreshold?: number;
  numberPlaceholder?: string;
}

const FilterPill = ({
  type = "select",
  label,
  options = [],
  value,
  onChange,
  onClick,
  selectedTab,
  avatar,
  selectedLabel,
  onLabelChange,
  limitFutureDays,
  showClearIcon = true,
  readOnly = false,
  groupBy,
  displayAll = false,
  parent_page,
  enableAccountSearch = false,
  forceOpen,
  onOpenChange,
  datePresetOptions,
  showDateMenuHeader = false,
  maxRangeInMonths,
  columnKey,
  isEnhancedLabel: isEnhancedLabelProp,
  useColumnFilter = false,
  singleSelection= false,
  onSearchChange,
  isLoading = false,
  hideUntilSearchThreshold,
  numberPlaceholder,
}: FilterPillProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<FilterDatePickerHandle>(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isInfoIconHovered, setIsInfoIconHovered] = useState(false);
  const isStatusPill =
  typeof label === "string" && label.toLowerCase() === "status";
  const [searchText, setSearchText] = useState("");
  const isAccountSearchDropdown = enableAccountSearch && typeof label === "string" && label.toLowerCase() === "account";
  const shouldHideUntilSearch =
  isAccountSearchDropdown && options.length > 5 && searchText.trim() === "";

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;

    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText]);


  const allOptionValues = options.map((o) => o.value);

  const isAllSelected =
    displayAll &&
    isStatusPill &&
    Array.isArray(value) &&
    value.length > 0 &&
    value.length === allOptionValues.length &&
    allOptionValues.every((v) => value.includes(v));

  const [filterDateRange, setFilterDateRange] = useState({
    preset: "",
    startDate: "",
    endDate: "",
  });

  const isOverdueSelected =
  type === "date" &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  "overdue" in value &&
  (value as DateRangeValue).overdue === true;
  const defaultPresets = [
    "currentWeek",
    "today",
    "currentMonth",
    "lastWeek",
    "lastMonth",
  ];
  const [defaultPresetInfo, setDefaultPresetInfo] = useState<{
    preset: string;
    startDate: string;
    endDate: string;
    overdue: boolean;
  } | null>(null);

  const normalizedLabel =
    typeof label === "string"
      ? label.replace(/\s+/g, "").toLowerCase()
      : "";
  const normalizedPillKey =
    typeof columnKey === "string" && columnKey.length > 0
      ? columnKey
      : typeof label === "string"
      ? label.replace(/\s+/g, "").toLowerCase()
      : "";
    
  const defaultEnhancedLabels = [
    "assignee",
    "status",
    "coach",
    "sponsor",
    "priority",
    "account",
    "tags",
    "subscribers",
    "customertype",
    "spaces",
    "actions",
    "currentrating",
    "opportunityname",
  ];
  const isEnhancedLabel = Boolean(
    (isEnhancedLabelProp ||
      useColumnFilter ||
      (typeof label === "string" && defaultEnhancedLabels.includes(normalizedLabel))) &&
      type !== "text"
  );

  const isSettingsAccountSingle =
    parent_page === "settings_page" &&
    typeof label === "string" &&
    label.toLowerCase() === "account";

  const isStatusSingle =
    parent_page === "accounts_page" &&
    typeof label === "string" &&
    label.toLowerCase() === "status";
  
  const pillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (type === "date" && typeof value === "object" && !Array.isArray(value)) {
    if ("preset" in value && value.preset && defaultPresets.includes(value.preset) && !defaultPresetInfo) {
      setDefaultPresetInfo({
        preset: value.preset,
        startDate: value.startDate || "",
        endDate: value.endDate || "",
        overdue: (value as DateRangeValue).overdue || false,
      });
    }
  }
  }, [type, value, defaultPresetInfo]);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      if (onSearchChange) {
        onSearchChange("");
      }
    }
  }, [open]);

  useEffect(() => {
    if (forceOpen !== undefined) {
      setOpen(forceOpen);
    }
  }, [forceOpen]);
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isClickInsidePill = ref.current?.contains(e.target as Node);
      const isClickInsideDropdown = popperRef.current?.contains(e.target as Node);

      if (!isClickInsidePill && !isClickInsideDropdown) {
        setOpen(false);
        onOpenChange?.(false);
        setLabelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    onOpenChange?.(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === "date" &&  defaultPresetInfo) {
      setFilterDateRange(defaultPresetInfo);
      onChange(undefined);
      setOpen(false);
      onOpenChange?.(false);
      return;
    }
    if (type !== "number") {
      setOpen(false);
      onOpenChange?.(false);
    }
    onChange(undefined);
    setOpen(false);
    onOpenChange?.(false);
    setIsHovered(false);
  };

  const isDateActive =
    type === "date" &&
    ((type === "date" && typeof value === "object" && !Array.isArray(value)) ||
      filterDateRange.preset !== "");

  const isSelectActive =
  type === "number"
    ? !!(
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (value as NumberFilterValue).value
      )
    : type !== "date" && !!value;

  const isSpacesPill = typeof label === "string" && label.toLowerCase() === "spaces";
  const hasSelectedSpaces =
    normalizedLabel === "spaces" &&
    Array.isArray(value) &&
    value.length > 0;
  const showAddIcon =
    !isSpacesPill &&
    !open &&
    !(isDateActive || isSelectActive);
  const showCloseIcon = open || isDateActive || isSelectActive;
  const formatDateMMMDDYYYY = (date: string) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  let displayValue: string | undefined =
    typeof value === "string" ? value : undefined;

  if (isOverdueSelected) {
    displayValue = "As of Today";
  } else if (
    type === "date" &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const range = value as DateRangeValue;

    if (
      (parent_page === "settings_page" || range.preset === "last3MonthsFull") &&
      range.startDate &&
      range.endDate
    ) {
      displayValue = `${formatDateMMMDDYYYY(range.startDate)} – ${formatDateMMMDDYYYY(range.endDate)}`;
    } else if (range.preset === "custom") {
      displayValue = "Custom";
    } else if (range.preset) {
      displayValue = formatPresetLabel(range.preset);
    }
  }
  else if (type !== "date" && typeof value === "string") {
    const selectedOption = options.find((opt) => opt.value === value);
    displayValue = selectedOption
      ? selectedOption.label
      : capitalizeFirstLetter(value);
  }
  else if (
    type === "number" &&
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const numVal = value as NumberFilterValue;
    const operatorLabel =
      numVal.operator === "gt"
        ? "Greater Than"
        : numVal.operator === "lt"
        ? "Less Than"
        : "";
    displayValue =
      operatorLabel && numVal.value
        ? `${operatorLabel} $${numVal.value.toLocaleString()}`
        : operatorLabel || undefined;
  }

  // Sync internal state with parent prop changes
  useEffect(() => {
    // Reset state completely when switching tab or label (e.g., "Meeting Date" ↔ "Shared Date")
    if (selectedTab || label) {
      if (type === "date" && typeof value === "object" && !Array.isArray(value)) {
        const dateRange = value as DateRangeValue;
        setFilterDateRange({
          preset: dateRange?.preset || "",
          startDate: dateRange?.startDate || "",
          endDate: dateRange?.endDate || "",
        });
      } else {
        setFilterDateRange({ preset: "", startDate: "", endDate: "" });
      }
    }
  }, [selectedTab, label]);

  const [pillSelected, setPillSelected] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      // Initialize selected values on open
      if (typeof value === "string") setPillSelected([value]);
      else if (Array.isArray(value)) setPillSelected(value);
      else setPillSelected([]);
    }
  }, [open, value]);

  // Inside FilterPill
  const popperRef = useRef<HTMLDivElement>(null);

  const selectedOption =
    typeof value === "string"
      ? options.find((opt) => opt.value === value)
      : Array.isArray(value)
      ? options.find((opt) => opt.value === value[0])
      : undefined;
  const avatarValue =
  selectedOption?.entityType === "space"
    ? selectedOption?.label          // space → name
    : selectedOption?.email;

  const getFirstName = (name?: string) => {
    if (!name) return "";
    return name.trim().split(" ")[0];
  };

  const getFormattedDisplayValue = () => {
    if (groupBy === ActionItemGroupByKey.RESOURCES && selectedOption?.label) {
      return `${getFirstName(selectedOption.label)}`;
    }
    return "";
  };

  const resolveDisplayLabel = (rawValue: string) =>
    options.find((opt) => opt.value === rawValue)?.label || rawValue;

  const shouldShowAvatar =
  (typeof label === "string" &&
    ["assignee", "coach", "sponsor", "subscribers"].includes(normalizedLabel)) ||
  (normalizedLabel === "spaces" &&
    Array.isArray(value) &&
    value.length > 0);

  return (
    <div className="fp-container" ref={ref}>
      <div
        className={`${
          isSpacesPill ? "fp-space-pill" : "fp-pill"
        } ${open ? "open" : ""} ${
          value || filterDateRange.preset ? "selected" : ""
        }`}
        ref={pillRef}
        onClick={(event) => {
          if (readOnly || isOverdueSelected) {
            event.stopPropagation(); // no dropdown
            return;
          }
          if (type === "date" && useColumnFilter) {
            setOpen(true);
            onOpenChange?.(true);
            return;
          }
          if (type === "date") {
            // Always mounted, so this works
            if (ref.current) {
              datePickerRef.current?.openDropdown(ref.current);
            }
          }
          if (type === "number") {
            setOpen(true);               // 🔥 always open
            onOpenChange?.(true);
            return;
          } else {
            setOpen((prev) => {
              const next = !prev;
              onOpenChange?.(next);
              return next;
            });
            if (onClick) onClick(event);
          }
        }}
      >
        {/* Icons */}
        {type !== "date" && label === "Group By" ? (
          value ? (
            <img
              src={isHovered ? HoveredIcon : FilledXicon}
              alt="clear"
              className="fp-icon"
              onClick={handleClear}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          ) : (
            <img src={GroupByIcon} alt="" className="fp-icon" />
          )
        ) : showAddIcon ? (
          <img src={FilledAddicon} alt="add" className="fp-icon" />
        ) : showCloseIcon &&
            showClearIcon &&
            !(
              type === "date" &&
              label === "Due Date"
            ) &&
            !(
              parent_page !== "report_page" &&
              type === "date" &&
              label === "Created Date"
            ) ? (
              <img
                src={isHovered ? HoveredIcon : FilledXicon}
                alt="close"
                className="fp-icon"
                onClick={handleClear}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              />
            ) : null}

        {/* Label */}
      <div className="fp-label-container">
        {Array.isArray(label) ? (
          <div
            className="fp-label"
            style={{ position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="fp-value"
              onClick={(e) => {
                e.stopPropagation();
                setLabelOpen((prev) => !prev);
              }}
            >
              {/* Display selected label (or first option by default) */}
          {selectedLabel ||
          (typeof label[0] === "string" ? label[0] : label[0].label)}
              <KeyboardArrowDownIcon
                fontSize="small"
                className="fp-arrow visible"
                sx={{marginLeft: 1}}
              />
            </div>

            {labelOpen && (
              <div
                className="fp-dropdown"
                style={{
                  position: "absolute",
                  top: "135%",
                  left: -20,
                  zIndex: 9999, 
                }}
              >
                {label.map((lbl) => {
            const labelText = typeof lbl === "string" ? lbl : lbl.label;
            const labelValue = typeof lbl === "string" ? lbl : lbl.value;

            return (
              <div
                key={labelValue}
                className={`fp-option ${
                  labelText === selectedLabel ? "active" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onLabelChange?.({
                    label: labelText,
                    value: labelValue,
                  });
                  setLabelOpen(false);
                }}
              >
                <div className="fp-option-content">
                  <h1 className="fp-dropdown-text">{labelText}</h1>
                </div>
                <span
                  className={`fp-option-circle ${
                    labelText === selectedLabel ? "filled" : "empty"
                  }`}
                ></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
        ) : (
          <span className="fp-label">
            {normalizedLabel === "spaces"
              ? hasSelectedSpaces
                ? "Spaces"
                : "Select Spaces"
              : typeof label === "string"
              ? label.toLowerCase() === "assignee"
                ? "Solo Player"
                : label.toLowerCase() === "coach"
                ? "Solo Coach"
                : label.toLowerCase() === "sponsor"
                ? "Exec Sponsor"
                : label.toLowerCase() === "description"
                ? "Title"
                : label
              : Array.isArray(label)
              ? label[0]
              : label}
          </span>
        )}

        {(
          (Array.isArray(value) ? value.length > 0 : !!value) ||
          displayValue || filterDateRange.preset
        ) && <span className="fp-divider"></span>}
      </div>

        {/* Value */}
        <span className="fp-value" style={{ display: "flex", alignItems: "center" }}>
        {enableAccountSearch &&
          typeof label === "string" &&
          label.toLowerCase() === "account" &&
          typeof value === "string" && (
            <span>
              {options.find((o) => o.value === value)?.label || value}
            </span>
        )}
          {type === "date" && displayValue && <span>{displayValue}</span>}
          {type === "number" && displayValue && (
            <span>{displayValue}</span>
          )}
          {isOverdueSelected && (
              <span className="fp-overdue">
                <span className="fp-divider"></span>
                {parent_page === "tasks-page" && (
                <img
                  src={isHovered ? HoveredIcon : FilledXicon}
                  alt="remove overdue"
                  style={{marginRight: "4px"}}
                  className="fp-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({
                      ...value,
                      overdue: false,
                      startDate: value.startDate,
                      endDate: value.endDate,
                    });
                    setIsHovered(false);
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
              )}
                <span>Overdue Tasks</span>
              </span>
            )}

          {shouldShowAvatar && (
          <>
            {groupBy !== ActionItemGroupByKey.RESOURCES && (
            <Avatar
              variant={selectedOption?.entityType === "space" ? "square" : "circular"}
              src={avatar || undefined}
              alt=""
              sx={{
                width: selectedOption?.entityType === "space" ? 14 : 18,
                height: selectedOption?.entityType === "space" ? 14 : 18,
                fontSize: 10,
                bgcolor: !avatar && typeof avatarValue === "string" ? getAvatarColor(avatarValue) : undefined,
              }}
            >
              {!avatar && typeof avatarValue === "string" && getAvatarInitials(avatarValue)}
            </Avatar>
          )}
          <span
            className="fp-text"
            style={{
              marginLeft:
                groupBy !== ActionItemGroupByKey.RESOURCES ? 6 : 0,
            }}
          >
            {groupBy === ActionItemGroupByKey.RESOURCES
              ? getFormattedDisplayValue()
              : ""}
          </span>
            {Array.isArray(value) && value.length > 1 && !((parent_page === "accounts_page" || parent_page==="client_overview_page")&& selectedOption?.entityType === "space") && (
              <span className="fp-count">+{value.length - 1}</span>
            )}
          </>
        )}

        {typeof label === "string" && !isAccountSearchDropdown &&
            !["assignee", "coach", "sponsor"].includes(normalizedPillKey) &&
            (isEnhancedLabel || defaultEnhancedLabels.includes(normalizedPillKey)) &&
            value && (
            <>
              {/* 🔹 SINGLE SELECT (string) */}
              {typeof value === "string" && (
                <>
                  {normalizedPillKey === "tags" ? (
                    <Box className="fp-tags-box">
                      <Typography sx={{ fontSize: "8px" }}>
                        {options.find((o) => o.value === value)?.label?.toLowerCase()}
                      </Typography>
                    </Box>
                  ) : (
                    <span>
                      {value === "no_account"
                        ? "No Account"
                        : options.find((o) => o.value === value)?.label}
                    </span>
                  )}
                </>
              )}

              {/* 🔹 MULTI SELECT (array) */}
              {Array.isArray(value) && value.length > 0 && (
                <>
                  {label.toLowerCase() === "tags" ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {value.slice(0, 1).map((val) => {
                        const option = options.find((o) => o.value === val);
                        return (
                          <Box key={val} className="fp-tags-box">
                            <Typography sx={{ fontSize: "8px" }}>
                              {option?.label?.toLowerCase()}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <span>
                      {isStatusPill && isAllSelected
                        ? "All"
                        : value[0] === "no_account"
                        ? "No Account"
                        : options.find((o) => o.value === value[0])?.label}
                    </span>
                  )}

                  {!isAllSelected && value.length > 1 && (
                    <span className="fp-count">+{value.length - 1}</span>
                  )}
                </>
              )}
            </>
          )}
          {typeof label === "string" &&
            !isEnhancedLabel &&
            !isAccountSearchDropdown &&
            type !== "date" && type !== "number" &&
            (displayValue || value) && (
              <span>
                {displayValue ??
                  (Array.isArray(value)
                    ? value.map((item) => resolveDisplayLabel(item)).join(", ")
                    : String(value))}
              </span>
            )}
        </span>

        {isOverdueSelected ? (
        <Tooltip title="Time range cannot be changed for overdue tasks." arrow placement="top" 
        componentsProps={{
          tooltip: {
            sx: {
              whiteSpace: "nowrap",
              maxWidth: "none",
            },
          },
        }}
        >
          <img
            src={isInfoIconHovered ? InfoIconHover : InfoIcon}
            alt="info"
            className="fp-info-icon"
            onMouseEnter={() => setIsInfoIconHovered(true)}
            onMouseLeave={() => setIsInfoIconHovered(false)}
          />
        </Tooltip>
      ) : (
        (open || value || filterDateRange.preset) &&
        !readOnly && (
          <KeyboardArrowDownIcon
            fontSize="small"
            className="fp-arrow visible"
          />
        )
      )}
      </div>

      {/* 🔹 Date Picker (always mounted for Date filter) */}
      {type === "date" && !useColumnFilter && (
        <FilterDatePicker
          ref={datePickerRef}
          onDateChange={(range) => {
            setFilterDateRange({
              preset: range.preset,
              startDate: range.startDate || "",
              endDate: range.endDate || "",
            });
            onChange(range);
          }}
          value={typeof value === "object" && !Array.isArray(value) && "preset" in value ? value : filterDateRange}
          limitFutureDays={limitFutureDays}
          presetOptions={datePresetOptions}
          showMenuHeader={showDateMenuHeader}
          maxRangeInMonths={maxRangeInMonths}
        />
      )}

      {/* 🔹 Normal dropdown for non-Date filters */}
      {(type !== "date" || useColumnFilter) && <Popper
        open={open}
        anchorEl={pillRef.current}
        placement="bottom-start"
        disablePortal={false}   // 🔥 VERY IMPORTANT
        sx={{zIndex: 999}}
        modifiers={[
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["top"],
            },
          },
        ]}
      >
        <div ref={popperRef}> {
        isAccountSearchDropdown ? (
          <div className="fp-dropdown fp-account-search-dropdown">
      
            {/* 🔍 Search Bar */}
            <div className="fp-search-wrapper">
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="fp-search-input"
                autoFocus
              />
            </div>
      
            {/* Options */}
            {shouldHideUntilSearch ? (
              <div className="fp-no-results">Type to search accounts</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`fp-option ${value === opt.value ? "active" : ""}`}
                  onClick={() => {
                    handleSelect(opt.value);
                    setOpen(false);
                  }}
                >
                  <div className="fp-option-content">
                    <h1 className="fp-dropdown-text">{opt.label}</h1>
                  </div>

                  <span
                    className={`fp-option-circle ${
                      value === opt.value ? "filled" : "empty"
                    }`}
                  ></span>
                </div>
              ))
      ) : (
        <div className="fp-no-results">No accounts found</div>
      )}
          </div>
        
        ) : type === "text" ? (
          <ColumnFilter
            columnKey={columnKey || "description"}
            currentValue={typeof value === "string" ? value : ""}
            onApply={(val: any) => {
              onChange(val);
            }}
            options={[]}
            type="multi"
            showSections={{
              sort: false,
              apply: false,
              options: false,
              search: true,  
            }}
            placeHolder="Search"
            enableSearchMode={true}
            isColumnFilter
            anchorElProp={pillRef.current}
            popperRef={popperRef}
          />
        ) : type === "number" ? (
          <ColumnFilter
            columnKey={typeof label === "string" ? label.toLowerCase() : ""}
            type="number"
            currentValue={
              value && typeof value === "object" && !Array.isArray(value)
                ? (value as NumberFilterValue)
                : undefined
            }
            onApply={(val) => {
              if (val === undefined) {
                onChange(undefined);
              } else if (typeof val === "string" || Array.isArray(val)) {
                onChange(val);
              } else if (val && typeof val === "object" && "operator" in val && "value" in val) {
                onChange(val as NumberFilterValue); // 🔥 sync parent with number filter
              } else if (val && typeof val === "object") {
                const selected = Object.keys(val).filter((key) => (val as Record<string, boolean>)[key]);
                onChange(selected);
              } else {
                onChange(undefined);
              }
              setOpen(false);
              onOpenChange?.(false);
            }}
            options={[]}
            isColumnFilter
            anchorElProp={pillRef.current}
            popperRef={popperRef}
            onOpen={() => {
              setOpen(true);
              onOpenChange?.(true);
            }}
            showSections={{
              sort: false,   // 🔥 THIS IS THE KEY
              search: false,
              apply: true,
              options: false,
            }}
            numberPlaceholder={numberPlaceholder}
          />
        ) : type === "date" && useColumnFilter ? (
          <ColumnFilter
            columnKey={columnKey || "date"}
            type="date"
            currentValue={
              value && typeof value === "object" && !Array.isArray(value) && "startDate" in value
                ? (value as DateRangeValue)
                : undefined
            }
            onApply={(val) => {
              if (
                val &&
                typeof val === "object" &&
                !Array.isArray(val) &&
                "startDate" in val &&
                "endDate" in val
              ) {
                onChange(val as DateRangeValue);
              } else {
                onChange(undefined);
              }
              setOpen(false);
              onOpenChange?.(false);
            }}
            options={[]}
            isColumnFilter
            anchorElProp={pillRef.current}
            popperRef={popperRef}
            showSections={{
              sort: false,
              search: false,
              apply: true,
              options: false,
            }}
          />
        ) : typeof label === "string" && isEnhancedLabel ? (
          <ColumnFilter
            columnKey={columnKey || (typeof label === "string" ? label.replace(/\s+/g, "").toLowerCase() : "")}
            currentValue={pillSelected}
            onApply={(val: string | string[] | DateRangeValue | Record<string, boolean> | NumberFilterValue | undefined) => {
              // Undefined -> clear
              if (val === undefined) {
                onChange(undefined);
              }
              // String or array -> pass through
              else if (typeof val === "string" || Array.isArray(val)) {
                onChange(val);
              }
              // NumberFilterValue -> pass through as number filter
              else if (val && typeof val === "object" && "operator" in val && "value" in val) {
                onChange(val as NumberFilterValue);
              }
              // Record<string, boolean> -> convert to selected keys
              else if (val && typeof val === "object") {
                const selected = Object.keys(val).filter((key) => (val as Record<string, boolean>)[key]);
                onChange(selected);
              } else {
                onChange(undefined);
              }
              setOpen(false);
            }}
            options={options.map((opt) => ({
              label: opt.label,
              value: opt.value,
              avatar: (opt as any).avatar,
              icon: (opt as any).icon,
              color: (opt as any).color,
              type: (opt as any).type,
              email: opt.email,
              entityType: opt.entityType,
              labelColour: (opt as any).labelColour,
            }))}
            type={isSettingsAccountSingle || isStatusSingle || singleSelection ? "single" : "multi"}
            showSections={{
              sort: false,
              search: Boolean(onSearchChange) || ["aeteam", "stage", "assignee", "coach", "sponsor", "account", "subscribers", "opportunityname"].includes(
                (columnKey || (typeof label === "string" ? label : "")).replace(/[\s_]+/g, "").toLowerCase()
              ),
              options: true,
              apply: true,
            }}
            isColumnFilter
            anchorElProp={pillRef.current}
            popperRef={popperRef}
            onSearchChange={onSearchChange}
            isLoading={isLoading}
            parent_page={parent_page}
            hideUntilSearchThreshold={hideUntilSearchThreshold}
          />
        ) : (
          <div className="fp-dropdown">
            {options.map((opt) => (
              <div
                key={opt.value}
                className={`fp-option ${value === opt.value ? "active" : ""}`}
                style={{ position: "relative" }}
                onClick={() => handleSelect(opt.value)}
              >
                <div className="fp-option-content">
                  {opt.labelColour && (
                    <span
                      style={{ background: opt.labelColour }}
                      className="fp-dropdown-colour-box"
                    ></span>
                  )}
                  <h1 className="fp-dropdown-text">{opt.label}</h1>
                </div>
                <span
                  className={`fp-option-circle ${
                    value === opt.value ? "filled" : "empty"
                  }`}
                ></span>
              </div>
            ))}
          </div>
        )}
        </div>
        </Popper>}
    </div>
  );
};

export default FilterPill;
